import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Permission } from '@marketing-os/shared';
import { AppShell } from '@/components/layout/AppShell';
import { ClientPortalShell } from '@/components/layout/ClientPortalShell';
import { LoginPage } from '@/features/auth/LoginPage';
import { ChangePasswordPage } from '@/features/auth/ChangePasswordPage';
import { AgencyDashboardPage } from '@/features/dashboard/AgencyDashboardPage';
import { ClientDashboardPage } from '@/features/dashboard/ClientDashboardPage';
import {
  NotFoundPage,
  PortalRedirect,
  ProtectedRoute,
  RequirePermission,
  RouteErrorBoundary,
} from './guards';

/* Módulos pesados carregados sob demanda. */
const ClientsListPage = lazy(() =>
  import('@/features/clients/ClientsListPage').then((m) => ({ default: m.ClientsListPage })),
);
const ClientCreatePage = lazy(() =>
  import('@/features/clients/ClientCreatePage').then((m) => ({ default: m.ClientCreatePage })),
);
const ClientDetailPage = lazy(() =>
  import('@/features/clients/ClientDetailPage').then((m) => ({ default: m.ClientDetailPage })),
);
const UsersPage = lazy(() => import('@/features/users/UsersPage').then((m) => ({ default: m.UsersPage })));
const CampaignsListPage = lazy(() =>
  import('@/features/campaigns/CampaignsListPage').then((m) => ({ default: m.CampaignsListPage })),
);
const CampaignWizardPage = lazy(() =>
  import('@/features/campaigns/CampaignWizardPage').then((m) => ({ default: m.CampaignWizardPage })),
);
const CampaignProjectPage = lazy(() =>
  import('@/features/campaigns/CampaignProjectPage').then((m) => ({ default: m.CampaignProjectPage })),
);
const CreativesGalleryPage = lazy(() =>
  import('@/features/creatives/CreativesGalleryPage').then((m) => ({ default: m.CreativesGalleryPage })),
);
const CreativeGeneratorPage = lazy(() =>
  import('@/features/creatives/CreativeGeneratorPage').then((m) => ({ default: m.CreativeGeneratorPage })),
);
const CreativeTemplatesPage = lazy(() =>
  import('@/features/creatives/CreativeTemplatesPage').then((m) => ({ default: m.CreativeTemplatesPage })),
);
const SocialCalendarPage = lazy(() =>
  import('@/features/social/SocialCalendarPage').then((m) => ({ default: m.SocialCalendarPage })),
);
const SocialComposerPage = lazy(() =>
  import('@/features/social/SocialComposerPage').then((m) => ({ default: m.SocialComposerPage })),
);
const SocialQueuePage = lazy(() =>
  import('@/features/social/SocialQueuePage').then((m) => ({ default: m.SocialQueuePage })),
);
const ContentTopicsPage = lazy(() =>
  import('@/features/content/ContentTopicsPage').then((m) => ({ default: m.ContentTopicsPage })),
);
const ContentPostsPage = lazy(() =>
  import('@/features/content/ContentPostsPage').then((m) => ({ default: m.ContentPostsPage })),
);
const ArticleEditorPage = lazy(() =>
  import('@/features/content/ArticleEditorPage').then((m) => ({ default: m.ArticleEditorPage })),
);
const EditorialCalendarPage = lazy(() =>
  import('@/features/content/EditorialCalendarPage').then((m) => ({ default: m.EditorialCalendarPage })),
);
const ConnectorsPage = lazy(() =>
  import('@/features/content/ConnectorsPage').then((m) => ({ default: m.ConnectorsPage })),
);
const RecommendationsPage = lazy(() =>
  import('@/features/optimization/RecommendationsPage').then((m) => ({ default: m.RecommendationsPage })),
);
const OptimizationSettingsPage = lazy(() =>
  import('@/features/optimization/OptimizationSettingsPage').then((m) => ({
    default: m.OptimizationSettingsPage,
  })),
);
const LeadsPage = lazy(() => import('@/features/leads/LeadsPage').then((m) => ({ default: m.LeadsPage })));
const FunnelPage = lazy(() => import('@/features/leads/FunnelPage').then((m) => ({ default: m.FunnelPage })));
const ReportsPage = lazy(() =>
  import('@/features/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const ApprovalsPage = lazy(() =>
  import('@/features/approvals/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })),
);
const IntegrationsPage = lazy(() =>
  import('@/features/integrations/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })),
);
const OrganizationSettingsPage = lazy(() =>
  import('@/features/settings/OrganizationSettingsPage').then((m) => ({
    default: m.OrganizationSettingsPage,
  })),
);
const AiPromptsPage = lazy(() =>
  import('@/features/settings/AiPromptsPage').then((m) => ({ default: m.AiPromptsPage })),
);
const AiCostsPage = lazy(() =>
  import('@/features/settings/AiCostsPage').then((m) => ({ default: m.AiCostsPage })),
);
const AuditLogsPage = lazy(() =>
  import('@/features/settings/AuditLogsPage').then((m) => ({ default: m.AuditLogsPage })),
);
const PortalDashboardPage = lazy(() =>
  import('@/features/portal/PortalDashboardPage').then((m) => ({ default: m.PortalDashboardPage })),
);
const PortalCalendarPage = lazy(() =>
  import('@/features/portal/PortalCalendarPage').then((m) => ({ default: m.PortalCalendarPage })),
);

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorBoundary /> },

  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      /* --------------------------- área da agência --------------------------- */
      {
        element: <PortalRedirect />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, element: <AgencyDashboardPage /> },
              { path: 'alterar-senha', element: <ChangePasswordPage /> },

              {
                path: 'clientes',
                element: <RequirePermission permission={Permission.CLIENT_READ} />,
                children: [
                  { index: true, element: <ClientsListPage /> },
                  {
                    path: 'novo',
                    element: (
                      <RequirePermission permission={Permission.CLIENT_MANAGE}>
                        <ClientCreatePage />
                      </RequirePermission>
                    ),
                  },
                  { path: ':clientId', element: <ClientDetailPage /> },
                  { path: ':clientId/painel', element: <ClientDashboardPage /> },
                ],
              },

              {
                path: 'aprovacoes',
                element: (
                  <RequirePermission permission={Permission.APPROVAL_READ}>
                    <ApprovalsPage />
                  </RequirePermission>
                ),
              },

              {
                path: 'campanhas',
                element: <RequirePermission permission={Permission.CAMPAIGN_READ} />,
                children: [
                  { index: true, element: <CampaignsListPage /> },
                  {
                    path: 'nova',
                    element: (
                      <RequirePermission permission={Permission.CAMPAIGN_DRAFT}>
                        <CampaignWizardPage />
                      </RequirePermission>
                    ),
                  },
                  { path: 'projetos/:projectId', element: <CampaignProjectPage /> },
                ],
              },

              {
                path: 'criativos',
                element: <RequirePermission permission={Permission.CREATIVE_READ} />,
                children: [
                  { index: true, element: <CreativesGalleryPage /> },
                  {
                    path: 'gerador',
                    element: (
                      <RequirePermission permission={Permission.CREATIVE_MANAGE}>
                        <CreativeGeneratorPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'templates',
                    element: (
                      <RequirePermission permission={Permission.TEMPLATE_MANAGE}>
                        <CreativeTemplatesPage />
                      </RequirePermission>
                    ),
                  },
                ],
              },

              {
                path: 'social',
                element: <RequirePermission permission={Permission.SOCIAL_READ} />,
                children: [
                  { index: true, element: <SocialCalendarPage /> },
                  { path: 'fila', element: <SocialQueuePage /> },
                  {
                    path: 'novo',
                    element: (
                      <RequirePermission permission={Permission.SOCIAL_MANAGE}>
                        <SocialComposerPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: ':postId',
                    element: (
                      <RequirePermission permission={Permission.SOCIAL_MANAGE}>
                        <SocialComposerPage />
                      </RequirePermission>
                    ),
                  },
                ],
              },

              {
                path: 'conteudo',
                element: <RequirePermission permission={Permission.CONTENT_READ} />,
                children: [
                  { index: true, element: <ContentTopicsPage /> },
                  { path: 'artigos', element: <ContentPostsPage /> },
                  { path: 'artigos/novo', element: <ArticleEditorPage /> },
                  { path: 'artigos/:postId', element: <ArticleEditorPage /> },
                  { path: 'calendario', element: <EditorialCalendarPage /> },
                  { path: 'conectores', element: <ConnectorsPage /> },
                ],
              },

              {
                path: 'otimizacao',
                element: <RequirePermission permission={Permission.RECOMMENDATION_READ} />,
                children: [
                  { index: true, element: <RecommendationsPage /> },
                  { path: 'configuracoes', element: <OptimizationSettingsPage /> },
                ],
              },

              {
                path: 'leads',
                element: <RequirePermission permission={Permission.CONVERSION_READ} />,
                children: [
                  { index: true, element: <LeadsPage /> },
                  { path: 'funil', element: <FunnelPage /> },
                ],
              },

              {
                path: 'relatorios',
                element: (
                  <RequirePermission permission={Permission.REPORT_READ}>
                    <ReportsPage />
                  </RequirePermission>
                ),
              },

              {
                path: 'integracoes',
                element: (
                  <RequirePermission permission={Permission.INTEGRATION_READ}>
                    <IntegrationsPage />
                  </RequirePermission>
                ),
              },

              {
                path: 'usuarios',
                element: (
                  <RequirePermission permission={Permission.USER_READ}>
                    <UsersPage />
                  </RequirePermission>
                ),
              },

              {
                path: 'configuracoes',
                children: [
                  {
                    index: true,
                    element: (
                      <RequirePermission permission={Permission.ORG_MANAGE}>
                        <OrganizationSettingsPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'prompts',
                    element: (
                      <RequirePermission permission={Permission.PROMPT_MANAGE}>
                        <AiPromptsPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'custos',
                    element: (
                      <RequirePermission permission={Permission.AI_COST_READ}>
                        <AiCostsPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'auditoria',
                    element: (
                      <RequirePermission permission={Permission.AUDIT_READ}>
                        <AuditLogsPage />
                      </RequirePermission>
                    ),
                  },
                ],
              },

              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },

      /* --------------------------- portal do cliente -------------------------- */
      {
        path: 'portal',
        element: <ClientPortalShell />,
        children: [
          { index: true, element: <PortalDashboardPage /> },
          {
            path: 'aprovacoes',
            element: (
              <RequirePermission permission={Permission.APPROVAL_READ}>
                <ApprovalsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'relatorios',
            element: (
              <RequirePermission permission={Permission.REPORT_READ}>
                <ReportsPage />
              </RequirePermission>
            ),
          },
          { path: 'calendario', element: <PortalCalendarPage /> },
          { path: 'alterar-senha', element: <ChangePasswordPage /> },
          { path: '*', element: <Navigate to="/portal" replace /> },
        ],
      },
    ],
  },
]);
